import React, { useMemo, useState, useEffect } from 'react';
import Timeline from 'react-calendar-timeline';
import 'react-calendar-timeline/styles.css';
import dayjs from 'dayjs';
import './InteractiveTimeline.css';
import { CButton, CButtonGroup } from '@coreui/react';
import { useTranslation } from 'react-i18next';

const formatDuration = (ms) => {
    if (ms < 0) ms = 0;
    const totalSeconds = Math.floor(ms / 1000);
    const h = Math.floor(totalSeconds / 3600).toString().padStart(2, '0');
    const m = Math.floor((totalSeconds % 3600) / 60).toString().padStart(2, '0');
    const s = Math.floor(totalSeconds % 60).toString().padStart(2, '0');
    return `${h}:${m}:${s}`;
};

const MIN_ZOOM = 60 * 1000; 
const MAX_ZOOM = 3 * 24 * 60 * 60 * 1000; 

const shiftToBrowserLocal = (isoString) => {
    if (!isoString) return 0;
    const d = new Date(isoString);
    return new Date(
        d.getUTCFullYear(),
        d.getUTCMonth(),
        d.getUTCDate(),
        d.getUTCHours() + 3,
        d.getUTCMinutes(),
        d.getUTCSeconds(),
        d.getUTCMilliseconds()
    ).getTime();
};

const InteractiveTimeline = ({ timelineData, dateRange, onItemDoubleClick, classIdToMeta, initialCenterTime }) => {
    const { t, i18n } = useTranslation();
    const [visibleTimeStart, setVisibleTimeStart] = useState(null);
    const [visibleTimeEnd, setVisibleTimeEnd] = useState(null);
    const [isTimelineReady, setIsTimelineReady] = useState(false);

    useEffect(() => {
        if (Timeline && Timeline.setDayjsLocale) {
            Timeline.setDayjsLocale(i18n.language);
        }
    }, [i18n.language]);

    const rangeStart = dateRange?.start;
    const rangeEnd = dateRange?.end;
    const dataLength = timelineData?.length || 0;
    const firstSegmentStart = timelineData?.[0]?.start;

    useEffect(() => {
        setIsTimelineReady(false);

        if (!rangeStart || !rangeEnd) {
            return;
        }

        const fullRangeStart = shiftToBrowserLocal(rangeStart);
        const fullRangeEnd = shiftToBrowserLocal(rangeEnd);

        // Определяем центр для масштабирования (либо куда кликнули, либо начало)
        let centerTime = null;
        if (initialCenterTime) {
            centerTime = shiftToBrowserLocal(initialCenterTime);
        } else if (dataLength > 0 && firstSegmentStart) {
            centerTime = shiftToBrowserLocal(firstSegmentStart);
        }

        if (centerTime) {
            const initialZoomDuration = 4 * 60 * 60 * 1000; // Показываем 4 часа по умолчанию

            let newVisibleStart = centerTime - (initialZoomDuration / 2);
            let newVisibleEnd = centerTime + (initialZoomDuration / 2);

            // Если выходим за границы суток - корректируем
            if (newVisibleStart < fullRangeStart) {
                newVisibleStart = fullRangeStart;
                newVisibleEnd = Math.min(newVisibleStart + initialZoomDuration, fullRangeEnd);
            } else if (newVisibleEnd > fullRangeEnd) {
                newVisibleEnd = fullRangeEnd;
                newVisibleStart = Math.max(newVisibleEnd - initialZoomDuration, fullRangeStart);
            }

            setVisibleTimeStart(newVisibleStart);
            setVisibleTimeEnd(newVisibleEnd);
        } else {
            setVisibleTimeStart(fullRangeStart);
            setVisibleTimeEnd(fullRangeEnd);
        }

        const timer = setTimeout(() => setIsTimelineReady(true), 50);
        return () => clearTimeout(timer);
        
    }, [rangeStart, rangeEnd, dataLength, firstSegmentStart, initialCenterTime]);

    const { groups, items } = useMemo(() => {
        if (!timelineData || timelineData.length === 0) {
            return { groups: [], items: [] };
        }

        const timelineGroups = [{ id: 1, title: t('common.post') || 'Post' }];

        const timelineItems = timelineData.map((segment, index) => {
            const meta = classIdToMeta[segment.class_id] || classIdToMeta.default;
            
            const startMs = shiftToBrowserLocal(segment.start);
            const endMs = shiftToBrowserLocal(segment.end);
            const durationMs = endMs - startMs;

            let title = `${meta.label}: ${formatDuration(durationMs)}`;
            if (segment.people_count > 0) {
                title = `${meta.label} (max ${segment.people_count}): ${formatDuration(durationMs)}`;
            }

            return {
                id: index,
                group: 1,
                title: title,
                start_time: startMs,
                end_time: endMs,
                canMove: false,
                canResize: false,
                itemProps: {
                    style: {
                        background: meta.color,
                        border: 'none',
                        color: (segment.class_id === -1 || segment.class_id === -2) ? '#333' : 'white',
                        textOverflow: 'ellipsis',
                        overflow: 'hidden',
                        whiteSpace: 'nowrap',
                        cursor: 'pointer'
                    }
                }
            };
        });

        return { groups: timelineGroups, items: timelineItems };
    }, [timelineData, classIdToMeta, t]);

    const handleTimeChange = (visibleTimeStart, visibleTimeEnd) => {
        setVisibleTimeStart(visibleTimeStart);
        setVisibleTimeEnd(visibleTimeEnd);
    };
    
    const handleZoom = (factor) => {
        if (visibleTimeStart === null || visibleTimeEnd === null || !dateRange || !dateRange.start || !dateRange.end) {
            return;
        }

        const fullRangeStart = shiftToBrowserLocal(dateRange.start);
        const fullRangeEnd = shiftToBrowserLocal(dateRange.end);
        const totalDuration = fullRangeEnd - fullRangeStart;

        const visibleTime = visibleTimeEnd - visibleTimeStart;
        let newVisibleTime = visibleTime * factor;
        
        if (newVisibleTime < MIN_ZOOM) newVisibleTime = MIN_ZOOM;
        if (newVisibleTime > MAX_ZOOM) newVisibleTime = MAX_ZOOM;
        if (newVisibleTime > totalDuration) newVisibleTime = totalDuration;
        
        const center = visibleTimeStart + visibleTime / 2;
        let newVisibleStart = center - newVisibleTime / 2;
        
        if (newVisibleStart < fullRangeStart) newVisibleStart = fullRangeStart;
        if (newVisibleStart + newVisibleTime > fullRangeEnd) newVisibleStart = fullRangeEnd - newVisibleTime;

        const newVisibleEnd = newVisibleStart + newVisibleTime;
        
        setVisibleTimeStart(newVisibleStart);
        setVisibleTimeEnd(newVisibleEnd);
    };

    if (!timelineData) {
        return null;
    }

    if (items.length === 0) {
        return <p className="text-center text-muted p-3">{t('camera.no_videos')}</p>;
    }

    if (!isTimelineReady) {
        return <div className="text-center p-3 text-muted">{t('timeline.loading')}</div>;
    }

    return (
        <div>
            <div className="timeline-controls">
                <CButtonGroup role="group" aria-label="Timeline zoom controls">
                    <CButton color="secondary" variant="outline" onClick={() => handleZoom(1.5)}>–</CButton>
                    <CButton color="secondary" variant="outline" onClick={() => handleZoom(0.5)}>+</CButton>
                </CButtonGroup>
                <span className="timeline-hint">
                    {t('timeline.zoom_hint')}
                </span>
            </div>
            <Timeline
                groups={groups}
                items={items}
                visibleTimeStart={visibleTimeStart}
                visibleTimeEnd={visibleTimeEnd}
                onTimeChange={handleTimeChange}
                onItemDoubleClick={onItemDoubleClick}
                sidebarWidth={0}
                lineHeight={50}
                itemHeightRatio={0.75}
                canChangeGroup={false}
                wheelZoomModifierKey="Ctrl"
                minZoom={MIN_ZOOM}
                maxZoom={MAX_ZOOM}
                buffer={1}
            />
        </div>
    );
};

export default InteractiveTimeline;